import type React from 'react'
import type { UnifiedSelectionResizeHandle } from '@/lib/transforms/types/unified-selection-resize-handle'
import { isCornerResizeHandle } from '@/lib/transforms/unified-selection-oriented-resize'
import { getResizeCursorForHandle } from '@/lib/transforms/selection-resize-cursor'
import { CROP_ACCENT_COLOR } from '../constants/crop-chrome'
import type { CropVisualRect } from '../helpers/badge-layout'

const RESIZE_HANDLES: UnifiedSelectionResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

function handleCenter(
  handle: UnifiedSelectionResizeHandle,
  visual: CropVisualRect
): { x: number; y: number } {
  const { x: vx, y: vy, width: vw, height: vh } = visual
  const hx = handle.includes('w') ? vx : handle.includes('e') ? vx + vw : vx + vw / 2
  const hy = handle.includes('n') ? vy : handle.includes('s') ? vy + vh : vy + vh / 2

  return { x: hx, y: hy }
}

interface CropResizeDotsProps {
  visual: CropVisualRect
  /** Dot radius / half-side (canvas units). */
  handleHalf: number
  /** Stroke width (canvas units). */
  sw: number
  /** Shape rotation (deg) — orients each dot's resize cursor. */
  rotation: number
  onResizeDown: (e: React.PointerEvent, handle: UnifiedSelectionResizeHandle) => void
  onPointerMove: (e: React.PointerEvent) => void
  onPointerUp: (e: React.PointerEvent) => void
}

/** Whole-shape resize handles — resize the entire frame, not individual path nodes. Corners are
 *  squares, edges are circles. */
export function CropResizeDots({
  visual,
  handleHalf,
  sw,
  rotation,
  onResizeDown,
  onPointerMove,
  onPointerUp,
}: CropResizeDotsProps) {
  return (
    <>
      {RESIZE_HANDLES.map((handle) => {
        const { x: hx, y: hy } = handleCenter(handle, visual)
        const isCorner = isCornerResizeHandle(handle)
        const cursor = getResizeCursorForHandle(handle, rotation)

        // Shared paint + interaction, appended after each variant's own geometry.
        const dotProps = {
          fill: 'white',
          stroke: CROP_ACCENT_COLOR,
          strokeWidth: sw,
          style: { pointerEvents: 'all' as const, cursor },
          onPointerDown: (e: React.PointerEvent) => onResizeDown(e, handle),
          onPointerMove,
          onPointerUp,
        }

        return isCorner ? (
          <rect
            key={handle}
            x={hx - handleHalf}
            y={hy - handleHalf}
            width={handleHalf * 2}
            height={handleHalf * 2}
            {...dotProps}
          />
        ) : (
          <circle key={handle} cx={hx} cy={hy} r={handleHalf} {...dotProps} />
        )
      })}
    </>
  )
}
